import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Col,
  DatePicker,
  Drawer,
  Empty,
  Modal,
  notification,
  Pagination,
  Progress,
  Row,
  Skeleton,
  Spin,
  Switch,
  Tag,
  Timeline,
  Tooltip,
} from "antd";
import {
  CheckCircleFilled,
  CheckCircleOutlined,
  CloseCircleFilled,
  EyeFilled,
  HistoryOutlined,
  InfoCircleOutlined,
  LockOutlined,
  TeamOutlined,
  WarningFilled,
  UserOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { post } from "../../../services/services";
import { ApiURL } from "../../../services/apiConstants";
import ViolationBadge from "../../../components/ViolationBadge";

/* ─── helpers ────────────────────────────────────────────────── */
const violationColor = (n) => (n === 0 ? "#52c41a" : n <= 5 ? "#fa8c16" : "#f5222d");

const initials = (name = "") =>
  name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");

const avatarColor = (name = "") => {
  const colors = ["#1890ff", "#722ed1", "#13c2c2", "#fa8c16", "#eb2f96", "#52c41a"];
  const idx = name.charCodeAt(0) % colors.length;
  return colors[idx];
};

const formatApplicability = (applicability = "") => {
  const labels = {
    all: "All Seafarers",
    under_18: "Seafarers Under 18",
    under18: "Seafarers Under 18",
  };
  return labels[applicability] || applicability.replaceAll("_", " ").replace(/\b\w/g, (c) => c.toUpperCase()) || "All Seafarers";
};

const getResponseError = (response, fallback) =>
  response?.response?.data?.message ||
  response?.response?.data?.error ||
  response?.message ||
  response?.error ||
  fallback;

const RulesSummarySkeleton = () => (
  <Row gutter={[10, 10]} style={{ marginTop: 18 }}>
    {[...Array(3)].map((_, i) => (
      <Col xs={24} sm={8} key={i}>
        <Skeleton.Button active block style={{ height: 80, borderRadius: 12 }} />
      </Col>
    ))}
  </Row>
);

const RuleCardSkeleton = () => (
  <Card size="small" style={{ borderRadius: 14, marginBottom: 10 }} styles={{ body: { padding: 16 } }}>
    <Skeleton active paragraph={{ rows: 4 }} />
  </Card>
);

const RulesGridSkeleton = () => (
  <Row gutter={[10, 10]} style={{ marginTop: 20 }}>
    {[...Array(2)].map((_, i) => (
      <Col xs={24} lg={12} key={i}>
        <RuleCardSkeleton />
      </Col>
    ))}
  </Row>
);


const StatCard = ({ label, value, color, icon, sub }) => (
  <Card
    style={{
      borderRadius: 14,
      border: "none",
      boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
      height: "100%",
    }}
    styles={{ body: { padding: "18px 20px" } }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: 12,
          background: `${color}18`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 22,
          color,
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 26, fontWeight: 700, color, lineHeight: 1.1 }}>{value}</div>
        <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: "#aaa", marginTop: 1 }}>{sub}</div>}
      </div>
    </div>
  </Card>
);

const HoursBar = ({ rest, work }) => {
  const total = rest + work || 1;
  const restPct = Math.round((rest / total) * 100);
  return (
    <div style={{ fontSize: 11, color: "#888" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
        <span style={{ color: "#52c41a" }}>Rest {rest.toFixed(0)}h</span>
        <span style={{ color: "#1890ff" }}>Work {work.toFixed(0)}h</span>
      </div>
      <div style={{ display: "flex", borderRadius: 4, overflow: "hidden", height: 6, background: "#1890ff" }}>
        <div style={{ width: `${restPct}%`, background: "#52c41a" }} />
      </div>
    </div>
  );
};

const CrewCard = ({ member, onView, onViewViolations }) => {
  const hasRecord = !!member.record_status;
  return (
    <Card
      style={{
        borderRadius: 14,
        border: `1.5px solid ${member.is_compliant ? "#b7eb8f" : hasRecord ? "#ffccc7" : "#e8e8e8"}`,
        boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
        height: "100%",
      }}
      styles={{ body: { padding: "16px" } }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <Avatar
            size={40}
            style={{ background: avatarColor(member.staff_name), fontWeight: 600, flexShrink: 0 }}
          >
            {initials(member.staff_name)}
          </Avatar>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontWeight: 600,
                fontSize: 13,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: 120,
              }}
              title={member.staff_name}
            >
              {member.staff_name}
            </div>
            <div style={{ fontSize: 11, color: "#888" }}>{member.role}</div>
          </div>
        </div>
        {member.is_compliant ? (
          <CheckCircleFilled style={{ color: "#52c41a", fontSize: 18 }} />
        ) : (
          <CloseCircleFilled style={{ color: hasRecord ? "#f5222d" : "#d9d9d9", fontSize: 18 }} />
        )}
      </div>

      <div style={{ margin: "12px 0 8px" }}>
        {hasRecord ? (
          <HoursBar rest={member.total_rest_hours} work={member.total_work_hours} />
        ) : (
          <div
            style={{
              textAlign: "center",
              fontSize: 11,
              color: "#bbb",
              background: "#fafafa",
              borderRadius: 6,
              padding: "6px 0",
            }}
          >
            No record this month
          </div>
        )}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {member.total_violations > 0 && (
            <Tooltip title="Click to view violation details">
              <Badge
                count={member.total_violations}
                style={{ backgroundColor: violationColor(member.total_violations), fontSize: 10, cursor: "pointer" }}
                onClick={onViewViolations}
              />
            </Tooltip>
          )}
          {member.record_status && (
            <Tag
              color={member.record_status === "closed" ? "green" : "orange"}
              style={{ fontSize: 10, padding: "0 5px", margin: 0 }}
            >
              {member.record_status}
            </Tag>
          )}
        </div>
        <Tooltip title="View Monthly Record">
          <Button
            type="text"
            size="small"
            icon={<EyeFilled />}
            style={{ color: "#1890ff" }}
            onClick={onView}
          />
        </Tooltip>
      </div>
    </Card>
  );
};

const RuleCard = React.memo(({ rule, canManage, updating, ruleNames, onToggle }) => {
  const mandatory = !!rule.is_mandatory;
  const enabled = mandatory || !!rule.is_enabled;
  const conflicts = rule.conflicting_rule_codes || [];
  const lastChanged = rule.last_changed_by;
  const toggleDisabled = mandatory || !canManage || rule.can_toggle === false;
  const [hovered, setHovered] = useState(false);

  const lastUpdatedTooltip = lastChanged ? (
    <div style={{ fontSize: 12 }}>
      <div style={{ marginBottom: 4 }}>
        <strong>Last Updated By</strong>
      </div>
      <div>
        Name: <strong>{lastChanged.staff_name || `Staff #${lastChanged.staff_id}`}</strong>
      </div>
      <div>
        Role: <strong>{lastChanged.role || "Unknown"}</strong>
      </div>
      {lastChanged.changed_at && (
        <div>
          Date: <strong>{dayjs(lastChanged.changed_at).format("DD MMM YYYY, HH:mm")}</strong>
        </div>
      )}
    </div>
  ) : null;

  return (
    <Card
      size="small"
      onMouseEnter={() => !toggleDisabled && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        height: "100%",
        borderRadius: 14,
        border: mandatory ? "1px solid #adc6ff" : "1px solid #f0f0f0",
        background: mandatory ? "#f5f7fa" : "#fff",
        boxShadow: hovered && !toggleDisabled
          ? "0 4px 16px rgba(0,0,0,0.12)"
          : mandatory ? "none" : "0 2px 10px rgba(0,0,0,0.05)",
        transition: "all 0.3s ease",
        cursor: "default",
        transform: hovered && !toggleDisabled ? "translateY(-2px)" : "translateY(0)",
      }}
      styles={{ body: { padding: 16, height: "100%" } }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, height: "100%" }}>
        <div style={{ minWidth: 0, display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 7 }}>
            <span style={{ fontWeight: 700, color: "#262626" }}>{rule.name || rule.code}</span>
            <Badge
              status={enabled ? "success" : "default"}
              text={enabled ? "Enabled" : "Disabled"}
            />
            {mandatory && (
              <Tag color="blue" icon={<LockOutlined />} style={{ margin: 0 }}>
                Mandatory
              </Tag>
            )}
          </div>

          <div style={{ color: "#737373", fontSize: 12, marginTop: 7, lineHeight: 1.5 }}>
            {rule.description || "No description available."}
          </div>

          <div style={{ fontSize: 11, color: "#595959", marginTop: 12 }}>
            <strong>Applicability:</strong> {formatApplicability(rule.applicability)}
          </div>

          {conflicts.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={{ color: "#8c8c8c", fontSize: 11, marginBottom: 5 }}>Conflicts With:</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                {conflicts.map((code) => (
                  <Tag color="orange" key={code} style={{ margin: 0, fontSize: 11 }}>
                    {ruleNames[code] || code.replaceAll("_", " ")}
                  </Tag>
                ))}
              </div>
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#a0a0a0", fontSize: 10, marginTop: "auto", paddingTop: 12 }}>
            {lastUpdatedTooltip ? (
              <Tooltip title={lastUpdatedTooltip}>
                <span style={{ cursor: "help", display: "flex", alignItems: "center", gap: 4 }}>
                  {lastChanged?.changed_at ? dayjs(lastChanged.changed_at).format("DD MMM YYYY, HH:mm") : "No update"} 
                  <InfoCircleOutlined style={{ fontSize: 10 }} />
                </span>
              </Tooltip>
            ) : (
              <span>No update history available</span>
            )}
          </div>
        </div>

        <Tooltip
          title={
            mandatory
              ? "Mandatory rules are always enabled"
              : !canManage
                ? "Only Captain and Chief Engineer can modify compliance settings"
                : rule.can_toggle === false
                  ? "This rule cannot be changed"
                  : undefined
          }
        >
          <span style={{ alignSelf: "flex-start" }} aria-label={`${mandatory ? "Enable" : "Toggle"} ${rule.name || rule.code} compliance rule`}>
            <Switch
              checked={enabled}
              disabled={toggleDisabled}
              loading={updating}
              checkedChildren={mandatory ? <LockOutlined /> : undefined}
              onChange={(checked) => onToggle(rule, checked)}
              style={enabled ? { background: mandatory ? "#1677ff" : "#52c41a" } : undefined}
            />
          </span>
        </Tooltip>
      </div>
    </Card>
  );
});

RuleCard.displayName = "RuleCard";

const RuleHistoryDrawer = React.memo(({ open, loading, history, onClose, onPageChange, currentPage, pageSize, total }) => {
  const startIdx = (currentPage - 1) * pageSize;
  const paginatedHistory = history.slice(startIdx, startIdx + pageSize);

  return (
    <Drawer
      title="Compliance Rules Audit History"
      placement="right"
      width={Math.min(520, window.innerWidth * 0.9)}
      open={open}
      onClose={onClose}
      styles={{ body: { paddingTop: 20, paddingBottom: 60 } }}
    >
      {loading ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : history.length === 0 ? (
        <Empty
          description="No Rule Changes Recorded"
          style={{ marginTop: 40 }}
        >
          <div style={{ fontSize: 12, color: "#999", marginTop: 8 }}>
            No compliance settings have been modified yet.
          </div>
        </Empty>
      ) : (
        <>
          <Timeline
            items={paginatedHistory.map((entry, index) => {
              const enabled = entry.enabled ?? entry.is_enabled ?? entry.action === "enabled";
              const changedBy = entry.changed_by || entry.last_changed_by || {};
              const staffName = entry.changed_by_staff_name || changedBy.staff_name || entry.staff_name || `Staff #${changedBy.staff_id || entry.staff_id || "Unknown"}`;
              const role = entry.changed_by_role || entry.role || changedBy.role || "Unknown role";
              const timestamp = entry.changed_at || entry.created_at || entry.timestamp;
              const previousState = entry.previous_state;
              const newState = entry.new_state;
              const reason = entry.reason || "user_toggle";

              return {
                key: entry.id || `${entry.rule_code || entry.code}-${timestamp || index}`,
                color: enabled ? "green" : "gray",
                dot: enabled ? <CheckCircleOutlined /> : <CloseCircleFilled />,
                children: (
                  <div style={{ paddingBottom: 12 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>
                      {entry.rule_name || entry.name || entry.rule_code || entry.code || "Compliance Rule"}
                    </div>
                    
                    {previousState || newState ? (
                      <div style={{ background: "#fafafa", borderRadius: 8, padding: "10px 12px", marginBottom: 8, fontSize: 12 }}>
                        <div style={{ color: "#666", marginBottom: 4 }}>
                          <strong>State Change:</strong>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <Tag color={previousState ? "gray" : "default"} style={{ margin: 0, fontSize: 11 }}>
                            {previousState === "enabled" || previousState ? "Enabled" : "Disabled"}
                          </Tag>
                          <span style={{ color: "#999" }}>→</span>
                          <Tag color={enabled ? "green" : "default"} style={{ margin: 0, fontSize: 11 }}>
                            {newState === "enabled" || enabled ? "Enabled" : "Disabled"}
                          </Tag>
                        </div>
                      </div>
                    ) : (
                      <Tag color={enabled ? "green" : "default"} style={{ margin: "6px 0", fontSize: 11 }}>
                        {enabled ? "Enabled" : "Disabled"}
                      </Tag>
                    )}

                    <div style={{ fontSize: 12, color: "#595959", marginBottom: 4 }}>
                      <strong>Changed By:</strong> {staffName}
                    </div>
                    <div style={{ fontSize: 12, color: "#595959", marginBottom: 4 }}>
                      <strong>Role:</strong> {role}
                    </div>
                    {timestamp && (
                      <div style={{ color: "#8c8c8c", fontSize: 11, marginBottom: 4 }}>
                        <strong>Date:</strong> {dayjs(timestamp).format("DD MMM YYYY, HH:mm")}
                      </div>
                    )}
                    {reason && (
                      <div style={{ background: "#fafafa", borderRadius: 8, padding: "8px 10px", marginTop: 8, fontSize: 11 }}>
                        <strong>Reason:</strong> {reason.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                      </div>
                    )}
                  </div>
                ),
              };
            })}
          />

          {total > pageSize && (
            <div style={{ marginTop: 20, paddingTop: 12, borderTop: "1px solid #f0f0f0" }}>
              <Pagination
                current={currentPage}
                pageSize={pageSize}
                total={total}
                onChange={onPageChange}
                size="small"
                style={{ textAlign: "center" }}
                aria-label="Audit history pagination"
              />
            </div>
          )}
        </>
      )}
    </Drawer>
  );
});

RuleHistoryDrawer.displayName = "RuleHistoryDrawer";

const ComplianceRulesCard = React.memo(({
  rules,
  canManage,
  loading,
  updatingRuleCodes,
  onToggle,
  onViewHistory,
  summary,
}) => {

  const mandatoryRules = useMemo(() => rules.filter((rule) => rule.is_mandatory), [rules]);
  const optionalRules = useMemo(() => rules.filter((rule) => !rule.is_mandatory), [rules]);
  
  const summaryData = useMemo(() => ({
    mandatory_count: summary?.mandatory_count ?? mandatoryRules.length,
    optional_enabled: summary?.optional_enabled ?? optionalRules.filter((rule) => rule.is_enabled).length,
    optional_disabled: summary?.optional_disabled ?? (optionalRules.length - (optionalRules.filter((rule) => rule.is_enabled).length)),
  }), [summary, mandatoryRules, optionalRules]);

  const ruleNames = useMemo(() => Object.fromEntries(rules.map((rule) => [rule.code, rule.name || rule.code])), [rules]);

  return (
    <Card
      style={{
        borderRadius: 16,
        border: "none",
        boxShadow: "0 3px 16px rgba(0,0,0,0.08)",
        marginBottom: 16,
      }}
      styles={{ body: { padding: "20px" } }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#262626" }}>Compliance Rules & Regulations</div>
          <div style={{ color: "#8c8c8c", fontSize: 12, marginTop: 3 }}>
            Configure optional regulations that participate in violation calculations.
          </div>
        </div>
        <Button 
          icon={<HistoryOutlined />} 
          onClick={onViewHistory} 
          style={{ borderRadius: 8 }}
          aria-label="View compliance audit history"
        >
          View Audit History
        </Button>
      </div>

      {loading ? (
        <>
          <RulesSummarySkeleton />
          <RulesGridSkeleton />
        </>
      ) : (
        <>
          <Row gutter={[10, 10]} style={{ marginTop: 18 }}>
            {[
              ["Mandatory", summaryData.mandatory_count, "#1677ff", "#e6f4ff"],
              ["Enabled", summaryData.optional_enabled, "#52c41a", "#f6ffed"],
              ["Disabled", summaryData.optional_disabled, "#8c8c8c", "#fafafa"],
            ].map(([label, count, color, background]) => (
              <Col xs={24} sm={8} key={label}>
                <div style={{ borderRadius: 12, background, padding: "12px 16px", border: `1px solid ${color}22` }}>
                  <div style={{ color, fontWeight: 800, fontSize: 22 }}>{count}</div>
                  <div style={{ color: "#737373", fontSize: 11 }}>{label} Rules</div>
                </div>
              </Col>
            ))}
          </Row>

          {!canManage && (
            <Alert
              type="info"
              showIcon
              message="Only Captain and Chief Engineer can modify compliance settings."
              style={{ borderRadius: 10, marginTop: 16 }}
              aria-label="Read-only access notification"
            />
          )}

          {rules.length === 0 ? (
            <Empty 
              description="No Compliance Rules Available" 
              style={{ margin: "40px 0 8px" }}
            >
              <div style={{ fontSize: 12, color: "#999", marginTop: 8 }}>
                No regulations have been configured for this ship.
              </div>
            </Empty>
          ) : (
            <>
              {mandatoryRules.length > 0 && (
                <div style={{ marginTop: 20 }}>
                  <div style={{ fontWeight: 700, color: "#434343", marginBottom: 10, fontSize: 14 }}>Mandatory Rules</div>
                  <Row gutter={[10, 10]}>
                    {mandatoryRules.map((rule) => (
                      <Col xs={24} lg={12} key={rule.id || rule.code}>
                        <RuleCard
                          rule={rule}
                          canManage={canManage}
                          updating={updatingRuleCodes.includes(rule.code)}
                          ruleNames={ruleNames}
                          onToggle={onToggle}
                        />
                      </Col>
                    ))}
                  </Row>
                </div>
              )}

              {optionalRules.length > 0 && (
                <div style={{ marginTop: 20 }}>
                  <div style={{ fontWeight: 700, color: "#434343", marginBottom: 10, fontSize: 14 }}>Optional Rules</div>
                  <Row gutter={[10, 10]}>
                    {optionalRules.map((rule) => (
                      <Col xs={24} lg={12} key={rule.id || rule.code}>
                        <RuleCard
                          rule={rule}
                          canManage={canManage}
                          updating={updatingRuleCodes.includes(rule.code)}
                          ruleNames={ruleNames}
                          onToggle={onToggle}
                        />
                      </Col>
                    ))}
                  </Row>
                </div>
              )}
            </>
          )}
        </>
      )}
    </Card>
  );
});

ComplianceRulesCard.displayName = "ComplianceRulesCard";

/* ─── main component ─────────────────────────────────────────── */
const RestHoursDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [role, setRole] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [rules, setRules] = useState([]);
  const [canManageRules, setCanManageRules] = useState(false);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [ruleSummary, setRuleSummary] = useState({
    mandatory_count: 0,
    optional_enabled: 0,
    optional_disabled: 0,
  });
  const [updatingRuleCodes, setUpdatingRuleCodes] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [ruleHistory, setRuleHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize] = useState(25);
  const [historyTotal, setHistoryTotal] = useState(0);

  const [conflictModal, setConflictModal] = useState({ open: false, rule: null, enabled: false });

  const [previousRuleState, setPreviousRuleState] = useState(null);

  const [violModal, setViolModal]     = useState({ open: false, member: null });
  const [violRecord, setViolRecord]   = useState(null);
  const [violLoading, setViolLoading] = useState(false);

  const openViolations = useCallback((member) => {
    setViolModal({ open: true, member });
    setViolRecord(null);
    setViolLoading(true);
    const FORM_DATA = new FormData();
    FORM_DATA.append("year", data?.year);
    FORM_DATA.append("month", data?.month);
    post(ApiURL.view_rest_hours_record_st, FORM_DATA).then((res) => {
      setViolRecord(res?.status === true ? res.data || null : null);
      setViolLoading(false);
    });
  }, [data]);

  useEffect(() => {
    fetchDashboard(dayjs());
    fetchRules();
  }, []);

  const fetchDashboard = async (month) => {
    setLoading(true);
    try {
      const FORM_DATA = new FormData();
      FORM_DATA.append("year", month.format("YYYY"));
      FORM_DATA.append("month", month.format("M"));
      const res = await post(ApiURL.rest_hours_dashboard_st, FORM_DATA);
      if (res?.status === true) {
        setData(res.data || null);
        setRole(res.role || "");
      }
    } catch (error) {
      notification.error({ message: "Unable to load dashboard", description: getResponseError(error, "Please try again.") });
    } finally {
      setLoading(false);
    }
  };

  const fetchRules = async () => {
    setRulesLoading(true);
    try {
      const res = await post(ApiURL.compliance_rules_st, new FormData());
      if (res?.status === true) {
        setRules(res.data?.rules || []);
        setCanManageRules(!!res.data?.can_manage);
        // Use backend summary counts
        if (res.data?.summary) {
          setRuleSummary({
            mandatory_count: res.data.summary.mandatory_count ?? 0,
            optional_enabled: res.data.summary.optional_enabled ?? 0,
            optional_disabled: res.data.summary.optional_disabled ?? 0,
          });
        }
      } else {
        throw res;
      }
    } catch (error) {
      setRules([]);
      setCanManageRules(false);
      notification.error({
        message: "Unable to load compliance rules",
        description: getResponseError(error, "Please try again."),
      });
    } finally {
      setRulesLoading(false);
    }
  };

  const toggleRule = async (rule, enabled) => {
    if (rule.is_mandatory || !canManageRules || rule.can_toggle === false) return;

    // Check for conflicts - show confirmation modal
    if (enabled && rule.conflicting_rule_codes && rule.conflicting_rule_codes.length > 0) {
      setConflictModal({ open: true, rule, enabled });
      return;
    }

    // Proceed with optimistic update
    await performToggleRule(rule, enabled);
  };

  const performToggleRule = async (rule, enabled) => {
    // Store previous state for rollback
    const previousRules = rules;
    setPreviousRuleState(previousRules);

    // Optimistic update
    const updatedRules = rules.map((r) =>
      r.code === rule.code ? { ...r, is_enabled: enabled } : r
    );
    setRules(updatedRules);

    // Show loading only on this rule
    setUpdatingRuleCodes((codes) => [...codes, rule.code]);

    try {
      const FORM_DATA = new FormData();
      FORM_DATA.append("rule_code", rule.code);
      FORM_DATA.append("enabled", String(enabled));
      const res = await post(ApiURL.update_compliance_rule_st, FORM_DATA);
      if (res?.status !== true) throw res;

      // Success notification
      const successMsg = `${rule.name || rule.code} has been ${enabled ? "enabled" : "disabled"}.`;
      notification.success({
        message: "Compliance rule updated",
        description: successMsg,
      });

      // Show disabled conflicts
      const disabledConflicts = res.data?.disabled_conflicts || [];
      if (disabledConflicts.length > 0) {
        const conflictNames = disabledConflicts.map(
          (code) => rules.find((item) => item.code === code)?.name || code.replaceAll("_", " ")
        );
        notification.warning({
          message: `${rule.name || rule.code} enabled successfully`,
          description: (
            <div>
              <div style={{ marginBottom: 4 }}>Automatically disabled:</div>
              <ul style={{ margin: "4px 0 0 20px", paddingLeft: 0 }}>
                {conflictNames.map((name) => (
                  <li key={name} style={{ fontSize: 12 }}>{name}</li>
                ))}
              </ul>
            </div>
          ),
          duration: 6,
        });
      }

      // Refresh both APIs to get latest state
      await Promise.all([fetchRules(), fetchDashboard(selectedMonth)]);
      setPreviousRuleState(null);
    } catch (error) {
      setRules(previousRules);
      notification.error({
        message: "Unable to update compliance rule",
        description: getResponseError(error, "The setting was not changed. Please try again."),
      });
      setPreviousRuleState(null);
    } finally {
      setUpdatingRuleCodes((codes) => codes.filter((code) => code !== rule.code));
    }
  };

  const fetchRuleHistory = async (page = 1) => {
    if (!historyOpen && page === 1) {
      setHistoryOpen(true);
    }
    setHistoryLoading(true);
    try {
      const FORM_DATA = new FormData();
      FORM_DATA.append("page", page);
      FORM_DATA.append("page_size", historyPageSize);
      const res = await post(ApiURL.compliance_rule_history_st, FORM_DATA);
      if (res?.status !== true) throw res;

      const history = Array.isArray(res.data)
        ? res.data
        : res.data?.history || res.data?.audit_logs || res.history || res.audit_logs || [];
      
      setRuleHistory(history);
      setHistoryPage(page);
      
      // Set total if available from backend
      if (res.data?.total) {
        setHistoryTotal(res.data.total);
      } else {
        setHistoryTotal(history.length);
      }
    } catch (error) {
      setRuleHistory([]);
      notification.error({
        message: "Unable to load audit history",
        description: getResponseError(error, "Please try again."),
      });
    } finally {
      setHistoryLoading(false);
    }
  };

  const crew = data?.crew || [];
  const own = data?.own;
  const totalViolations = crew.reduce((s, m) => s + (m.total_violations || 0), 0);
  const compliantCount = crew.filter((m) => m.is_compliant).length;
  const nonCompliantCount = crew.length - compliantCount;
  const compliancePct = crew.length ? Math.round((compliantCount / crew.length) * 100) : 0;
  const period = data ? dayjs(`${data.year}-${data.month}-01`).format("MMMM YYYY") : "";

  const goToRecord = useCallback((staffId) =>
    navigate("/records", { state: { staff_id: staffId, year: data?.year, month: data?.month } }), [data, navigate]);

  return (
    <>

      {/* ── Header bar ── */}
      <div
        style={{
          background: "linear-gradient(135deg, #0f2027, #203a43, #2c5364)",
          borderRadius: 16,
          padding: "20px 24px",
          marginBottom: 16,
          marginTop: 12,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <div style={{ color: "#ffffff99", fontSize: 12, letterSpacing: 1, textTransform: "uppercase" }}>
            Rest Hours Compliance
          </div>
          <div style={{ color: "#fff", fontSize: 22, fontWeight: 700, lineHeight: 1.2 }}>
            {period || "Loading…"}
          </div>
          {role && (
            <Tag
              style={{
                marginTop: 6,
                background: "#ffffff22",
                color: "#fff",
                border: "1px solid #ffffff44",
                borderRadius: 20,
                fontSize: 11,
              }}
            >
              {role}
            </Tag>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <DatePicker
            picker="month"
            value={selectedMonth}
            format="MMM YYYY"
            style={{ width: 130, borderRadius: 8 }}
            disabledDate={(c) => c && c > dayjs().endOf("month")}
            onChange={(val) => { if (val) setSelectedMonth(val); }}
          />
          <Button
            type="primary"
            loading={loading}
            style={{ borderRadius: 8, background: "#1890ff" }}
            onClick={() => fetchDashboard(selectedMonth)}
          >
            Search
          </Button>
        </div>
      </div>

      {/* ── Own record ── */}
      {own && (
        <Card
          style={{
            borderRadius: 16,
            marginBottom: 16,
            border: "none",
            boxShadow: "0 4px 20px rgba(0,0,0,0.10)",
            overflow: "hidden",
          }}
          styles={{ body: { padding: 0 } }}
        >
          <div style={{ display: "flex", flexWrap: "wrap" }}>
            {/* Left accent panel */}
            <div
              style={{
                background: own.is_compliant
                  ? "linear-gradient(135deg, #52c41a, #95de64)"
                  : "linear-gradient(135deg, #f5222d, #ff7875)",
                minWidth: 180,
                padding: "24px 20px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: 8,
                flex: "0 0 180px",
              }}
            >
              <Avatar
                size={56}
                style={{ background: "#ffffff33", fontSize: 22, fontWeight: 700, color: "#fff" }}
              >
                {initials(own.staff_name)}
              </Avatar>
              <div style={{ color: "#fff", fontWeight: 700, fontSize: 14, textAlign: "center" }}>
                {own.staff_name}
              </div>
              <div
                style={{
                  background: "#ffffff33",
                  color: "#fff",
                  borderRadius: 20,
                  padding: "2px 12px",
                  fontSize: 11,
                  fontWeight: 600,
                }}
              >
                {own.is_compliant ? "✓ Compliant" : "✗ Non-Compliant"}
              </div>
              {own.record_status && (
                <Tag
                  style={{
                    background: "#ffffff22",
                    color: "#fff",
                    border: "1px solid #ffffff44",
                    borderRadius: 20,
                    fontSize: 10,
                    textTransform: "capitalize",
                  }}
                >
                  {own.record_status}
                </Tag>
              )}
            </div>

            {/* Right metrics panel */}
            <div
              style={{
                flex: 1,
                padding: "20px 24px",
                display: "flex",
                flexWrap: "wrap",
                gap: 20,
                alignItems: "center",
              }}
            >
              <div style={{ textAlign: "center", minWidth: 100 }}>
                <Progress
                  type="circle"
                  size={80}
                  percent={Math.min(
                    100,
                    Math.round((own.total_rest_hours / ((own.total_rest_hours + own.total_work_hours) || 1)) * 100)
                  )}
                  strokeColor="#52c41a"
                  format={(p) => <span style={{ fontSize: 14, fontWeight: 700 }}>{p}%</span>}
                />
                <div style={{ fontSize: 11, color: "#888", marginTop: 6 }}>Rest Share</div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 10, flex: 1, minWidth: 180 }}>
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                    <span style={{ color: "#555" }}>
                      <ClockCircleOutlined style={{ color: "#52c41a", marginRight: 4 }} />
                      Rest Hours
                    </span>
                    <strong style={{ color: "#52c41a" }}>{own.total_rest_hours.toFixed(1)} hrs</strong>
                  </div>
                  <Progress
                    percent={Math.min(100, Math.round((own.total_rest_hours / 744) * 100))}
                    strokeColor="#52c41a"
                    showInfo={false}
                    size="small"
                  />
                </div>
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                    <span style={{ color: "#555" }}>
                      <ThunderboltOutlined style={{ color: "#1890ff", marginRight: 4 }} />
                      Work Hours
                    </span>
                    <strong style={{ color: "#1890ff" }}>{own.total_work_hours.toFixed(1)} hrs</strong>
                  </div>
                  <Progress
                    percent={Math.min(100, Math.round((own.total_work_hours / 744) * 100))}
                    strokeColor="#1890ff"
                    showInfo={false}
                    size="small"
                  />
                </div>
              </div>

              <Tooltip title={own.total_violations > 0 ? "Click to view your violations" : "No violations"}>
                <div
                  style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, cursor: own.total_violations > 0 ? "pointer" : "default" }}
                  onClick={() => own.total_violations > 0 && openViolations(own)}
                >
                  <div
                    style={{
                      fontSize: 36,
                      fontWeight: 800,
                      color: violationColor(own.total_violations),
                      lineHeight: 1,
                    }}
                  >
                    {own.total_violations}
                  </div>
                  <div style={{ fontSize: 11, color: "#888" }}>
                    {own.total_violations > 0 && <WarningFilled style={{ color: violationColor(own.total_violations), marginRight: 3 }} />}
                    Violations
                  </div>
                </div>
              </Tooltip>

              <Button
                type="primary"
                icon={<EyeFilled />}
                style={{ borderRadius: 8, alignSelf: "center" }}
                onClick={() => goToRecord(own.staff_id)}
              >
                My Record
              </Button>
            </div>
          </div>
        </Card>
      )}

      <ComplianceRulesCard
        rules={rules}
        canManage={canManageRules}
        loading={rulesLoading}
        updatingRuleCodes={updatingRuleCodes}
        onToggle={toggleRule}
        onViewHistory={() => fetchRuleHistory(1)}
        summary={ruleSummary}
      />

      {/* ── Crew summary stat cards ── */}
      {crew.length > 0 && (
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={12} sm={12} md={6}>
            <StatCard
              label="Total Crew"
              value={crew.length}
              color="#1890ff"
              icon={<TeamOutlined />}
              sub={`${period}`}
            />
          </Col>
          <Col xs={12} sm={12} md={6}>
            <StatCard
              label="Compliant"
              value={compliantCount}
              color="#52c41a"
              icon={<CheckCircleFilled />}
              sub={`${compliancePct}% of crew`}
            />
          </Col>
          <Col xs={12} sm={12} md={6}>
            <StatCard
              label="Non-Compliant"
              value={nonCompliantCount}
              color={nonCompliantCount > 0 ? "#f5222d" : "#52c41a"}
              icon={<CloseCircleFilled />}
              sub={nonCompliantCount > 0 ? "Action required" : "All clear"}
            />
          </Col>
          <Col xs={12} sm={12} md={6}>
            <StatCard
              label="Crew Violations"
              value={totalViolations}
              color={violationColor(totalViolations)}
              icon={<WarningFilled />}
              sub="Total across all crew"
            />
          </Col>
        </Row>
      )}

      {/* ── Compliance donut + crew cards ── */}
      {crew.length > 0 && (
        <Row gutter={[12, 12]}>
          {/* Compliance ring */}
          <Col xs={24} sm={24} md={6}>
            <Card
              style={{
                borderRadius: 14,
                border: "none",
                boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                height: "100%",
                minHeight: 200,
              }}
              styles={{ body: { padding: 20, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%" } }}
            >
              <div style={{ fontSize: 12, color: "#888", marginBottom: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Crew Compliance Rate
              </div>
              <Progress
                type="circle"
                size={130}
                percent={compliancePct}
                strokeColor={compliancePct === 100 ? "#52c41a" : compliancePct >= 60 ? "#fa8c16" : "#f5222d"}
                format={(p) => (
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 24, fontWeight: 800, lineHeight: 1 }}>{p}%</div>
                    <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>Compliant</div>
                  </div>
                )}
              />
              <div style={{ marginTop: 16, display: "flex", gap: 12 }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#52c41a" }}>{compliantCount}</div>
                  <div style={{ fontSize: 10, color: "#aaa" }}>OK</div>
                </div>
                <div style={{ width: 1, background: "#f0f0f0" }} />
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#f5222d" }}>{nonCompliantCount}</div>
                  <div style={{ fontSize: 10, color: "#aaa" }}>Issues</div>
                </div>
              </div>
            </Card>
          </Col>

          {/* Crew cards grid */}
          <Col xs={24} sm={24} md={18}>
            <Row gutter={[10, 10]}>
              {crew.map((member) => (
                <Col key={member.staff_id} xs={24} sm={12} md={12} lg={8}>
                  <CrewCard
                    member={member}
                    onView={() => goToRecord(member.staff_id)}
                    onViewViolations={() => openViolations(member)}
                  />
                </Col>
              ))}
            </Row>
          </Col>
        </Row>
      )}

      {crew.length === 0 && !loading && (
        <Card
          style={{ borderRadius: 14, textAlign: "center", marginTop: 16, border: "none", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}
        >
          <UserOutlined style={{ fontSize: 40, color: "#d9d9d9", marginBottom: 12 }} />
          <div style={{ color: "#aaa" }}>No crew data found for this month.</div>
        </Card>
      )}

      {/* Per-staff violations modal */}
      <Modal
        open={violModal.open}
        onCancel={() => setViolModal({ open: false, member: null })}
        footer={null}
        title={
          <span>
            STCW Violations —{" "}
            <strong>{violModal.member?.staff_name}</strong>
            <span style={{ color: "#888", fontWeight: 400, marginLeft: 8, fontSize: 13 }}>
              {data ? dayjs(`${data.year}-${data.month}-01`).format("MMMM YYYY") : ""}
            </span>
          </span>
        }
        width={580}
        styles={{ body: { maxHeight: "65vh", overflowY: "auto", paddingTop: 12 } }}
      >
        {violLoading ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Spin size="large" />
          </div>
        ) : (() => {
          const allViolations = (violRecord?.days || []).flatMap((d) =>
            (d.violations || []).map((v) => ({ ...v, date: d.date }))
          );
          if (!allViolations.length) return <Empty description="No violations this month" />;
          const byDate = allViolations.reduce((acc, v) => {
            (acc[v.date] = acc[v.date] || []).push(v);
            return acc;
          }, {});
          return Object.entries(byDate)
            .sort(([a], [b]) => dayjs(a).unix() - dayjs(b).unix())
            .map(([date, viols]) => (
              <div key={date} style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, color: "#555", fontSize: 12, marginBottom: 6, borderBottom: "1px solid #f0f0f0", paddingBottom: 4 }}>
                  {dayjs(date).format("DD MMM YYYY")}
                </div>
                {viols.map((v, i) => <ViolationBadge key={i} rule={v.rule} detail={v.detail} />)}
              </div>
            ));
        })()}
      </Modal>

      <RuleHistoryDrawer
        open={historyOpen}
        loading={historyLoading}
        history={ruleHistory}
        onClose={() => setHistoryOpen(false)}
        onPageChange={(page) => fetchRuleHistory(page)}
        currentPage={historyPage}
        pageSize={historyPageSize}
        total={historyTotal}
      />

      {/* ── Conflict Confirmation Modal ── */}
      <Modal
        title="Enable Compliance Rule"
        open={conflictModal.open}
        onOk={() => {
          performToggleRule(conflictModal.rule, conflictModal.enabled);
          setConflictModal({ open: false, rule: null, enabled: false });
        }}
        onCancel={() => setConflictModal({ open: false, rule: null, enabled: false })}
        okText="Enable"
        cancelText="Cancel"
        width={400}
      >
        <div style={{ color: "#666", lineHeight: 1.6 }}>
          <p>This rule conflicts with:</p>
          <ul style={{ marginLeft: 20 }}>
            {(conflictModal.rule?.conflicting_rule_codes || []).map((code) => {
              const conflictRule = rules.find((r) => r.code === code);
              return (
                <li key={code}>
                  {conflictRule?.name || code.replaceAll("_", " ")}
                </li>
              );
            })}
          </ul>
          <p style={{ marginTop: 12, fontSize: 12, color: "#999" }}>
            Enabling this rule may automatically disable conflicting rules.
          </p>
        </div>
      </Modal>
    </>
  );
};

export default RestHoursDashboard;
